<template>
    <div class="admin-page">
        <!-- Login Screen -->
        <div v-if="!user" class="login-container">
            <div class="login-box">
                <h1>ADMIN</h1>
                <div class="login-form">
                    <label for="email">EMAIL</label>
                    <input type="email" id="email" v-model="email" @keyup.enter="login"
                        placeholder="admin@nadezhda.com" />

                    <label for="password">PASSWORD</label>
                    <input type="password" id="password" v-model="password" @keyup.enter="login"
                        placeholder="••••••••" />

                    <p v-if="error" class="error">{{ error }}</p>

                    <button @click="login" :disabled="loading">
                        {{ loading ? 'SIGNING IN...' : 'SIGN IN' }}
                    </button>
                </div>
            </div>
        </div>

        <!-- Admin Dashboard -->
        <div v-else class="admin-dashboard">
            <header class="admin-header">
                <h1>NADEZHDA ADMIN</h1>
                <div class="header-actions">
                    <span class="user-email">{{ user.email }}</span>
                    <button class="logout-btn" @click="logout">LOGOUT</button>
                </div>
            </header>

            <nav class="admin-nav">
                <button :class="{ active: activeTab === 'shows' }" @click="activeTab = 'shows'">
                    SHOWS
                </button>
                <button :class="{ active: activeTab === 'content' }" @click="activeTab = 'content'">
                    CONTENT
                </button>
                <button :class="{ active: activeTab === 'merch' }" @click="activeTab = 'merch'">
                    MERCH
                </button>
                <button v-if="isSuperAdmin" :class="{ active: activeTab === 'tools' }" @click="activeTab = 'tools'">
                    TOOLS
                </button>
            </nav>

            <main class="admin-content">
                <!-- Shows Management -->
                <div v-if="activeTab === 'shows'" class="shows-section">
                    <div class="section-header">
                        <h2>MANAGE SHOWS</h2>
                        <button class="add-btn" @click="openAddShowModal">+ ADD SHOW</button>
                    </div>

                    <div class="shows-table">
                        <div v-if="loadingShows" class="loading">Loading shows...</div>
                        <div v-else-if="shows.length === 0" class="empty">AIN'T NO SHOWS</div>
                        <template v-else>
                            <div class="shows-header">
                                <span class="col-date sortable" @click="sortShowsBy('date')">
                                    DATE
                                    <span class="sort-indicator" v-if="showsSortBy === 'date'">{{ showsSortDirection ===
                                        'asc' ? '↑' : '↓' }}</span>
                                </span>
                                <span class="col-time sortable" @click="sortShowsBy('time')">
                                    TIME
                                    <span class="sort-indicator" v-if="showsSortBy === 'time'">{{ showsSortDirection ===
                                        'asc' ? '↑' : '↓' }}</span>
                                </span>
                                <span class="col-venue sortable" @click="sortShowsBy('venue')">
                                    VENUE
                                    <span class="sort-indicator" v-if="showsSortBy === 'venue'">{{ showsSortDirection
                                        === 'asc' ? '↑' : '↓' }}</span>
                                </span>
                                <span class="col-city sortable" @click="sortShowsBy('city')">
                                    CITY
                                    <span class="sort-indicator" v-if="showsSortBy === 'city'">{{ showsSortDirection ===
                                        'asc' ? '↑' : '↓' }}</span>
                                </span>
                                <span class="col-event sortable" @click="sortShowsBy('event')">
                                    EVENT
                                    <span class="sort-indicator" v-if="showsSortBy === 'event'">{{ showsSortDirection
                                        === 'asc' ? '↑' : '↓' }}</span>
                                </span>
                                <span class="col-support sortable" @click="sortShowsBy('support')">
                                    SUPPORT
                                    <span class="sort-indicator" v-if="showsSortBy === 'support'">{{ showsSortDirection
                                        === 'asc' ? '↑' : '↓' }}</span>
                                </span>
                                <span class="col-tickets">TICKETS</span>
                                <span class="col-actions">ACTIONS</span>
                            </div>
                            <div class="shows-row" v-for="show in sortedShows" :key="show.id">
                                <span class="col-date">{{ show.date }}</span>
                                <span class="col-time">{{ show.time }}</span>
                                <span class="col-venue">{{ show.venue }}</span>
                                <span class="col-city">{{ show.city }}</span>
                                <span class="col-event">
                                    <span v-if="show.event">{{ show.event }}</span>
                                    <span v-else class="no-data">—</span>
                                </span>
                                <span class="col-support">
                                    <span v-if="show.support">{{ show.support }}</span>
                                    <span v-else class="no-data">—</span>
                                </span>
                                <span class="col-tickets">
                                    <a v-if="show.tickets" :href="show.tickets" target="_blank">VIEW</a>
                                    <span v-else class="no-tickets">—</span>
                                </span>
                                <span class="col-actions">
                                    <button class="edit-btn" @click="editShow(show)">EDIT</button>
                                    <button class="delete-btn" @click="deleteShow(show)">DELETE</button>
                                </span>
                            </div>
                        </template>
                    </div>
                </div>

                <!-- Content Management -->
                <div v-if="activeTab === 'content'" class="content-section">
                    <div class="section-header">
                        <h2>MANAGE SITE CONTENT</h2>
                    </div>

                    <div v-if="loadingContent" class="loading">Loading content...</div>
                    <div v-else class="content-form">
                        <!-- Video Section -->
                        <div class="content-card">
                            <h3>MUSIC VIDEO</h3>
                            <label>VIDEO TITLE</label>
                            <input type="text" v-model="contentForm.videoTitle"
                                placeholder="BUSY BEING BORED VIDEO OUT NOW" />

                            <label>YOUTUBE URL</label>
                            <input type="text" v-model="contentForm.videoUrl"
                                placeholder="https://www.youtube.com/watch?v=... or embed URL" />
                            <p class="field-hint">Paste a YouTube link or embed URL</p>
                        </div>

                        <!-- Playlist Section -->
                        <div class="content-card">
                            <h3>SPOTIFY PLAYLIST</h3>
                            <label>PLAYLIST TITLE</label>
                            <input type="text" v-model="contentForm.playlistTitle" placeholder="BATTERY EP OUT NOW" />

                            <label>SPOTIFY EMBED URL</label>
                            <input type="text" v-model="contentForm.playlistUrl"
                                placeholder="https://open.spotify.com/embed/album/..." />
                            <p class="field-hint">Get embed URL from Spotify: Share → Embed → Copy</p>
                        </div>
                        <!-- Music Image Section -->
                        <div class="content-card">
                            <h3>MUSIC SECTION IMAGE</h3>
                            <div class="image-upload-container">
                                <div class="image-preview" v-if="contentForm.musicImageUrl || musicImagePreview">
                                    <img :src="musicImagePreview || contentForm.musicImageUrl" alt="Preview" />
                                </div>
                                <div class="image-upload-actions">
                                    <label class="upload-btn">
                                        <input type="file" accept="image/*" @change="handleMusicImageUpload"
                                            ref="musicImageInput" />
                                        {{ contentForm.musicImageUrl ? 'CHANGE IMAGE' : 'UPLOAD IMAGE' }}
                                    </label>
                                    <button v-if="contentForm.musicImageUrl" class="remove-btn"
                                        @click="removeMusicImage" type="button">
                                        REMOVE
                                    </button>
                                    <span v-if="uploadingMusicImage" class="upload-status">Uploading...</span>
                                </div>
                            </div>
                            <p class="field-hint">Leave empty to use default Battery image.</p>
                        </div>

                        <div class="content-actions">
                            <button class="save-btn save-btn-primary" @click="saveContent"
                                :disabled="savingContent || !hasContentChanges">
                                {{ savingContent ? 'SAVING...' : (hasContentChanges ? 'SAVE CHANGES' : 'NO CHANGES') }}
                            </button>
                        </div>
                    </div>
                </div>

                <!-- Merch Management -->
                <div v-if="activeTab === 'merch'" class="merch-section">
                    <div class="section-header">
                        <h2>MANAGE MERCH</h2>
                        <button class="add-btn" @click="openAddMerchModal">+ ADD ITEM</button>
                    </div>

                    <div class="merch-table">
                        <div v-if="loadingMerch" class="loading">Loading merch...</div>
                        <div v-else-if="merch.length === 0" class="empty">AIN'T NO MERCH</div>
                        <template v-else>
                            <div class="merch-header">
                                <span class="col-image">IMAGE</span>
                                <span class="col-name sortable" @click="sortMerchBy('name')">
                                    NAME
                                    <span class="sort-indicator" v-if="merchSortBy === 'name'">{{ merchSortDirection === 'asc' ? '↑' : '↓' }}</span>
                                </span>
                                <span class="col-status sortable" @click="sortMerchBy('status')">
                                    STATUS
                                    <span class="sort-indicator" v-if="merchSortBy === 'status'">{{ merchSortDirection === 'asc' ? '↑' : '↓' }}</span>
                                </span>
                                <span class="col-link sortable" @click="sortMerchBy('link')">
                                    LINK
                                    <span class="sort-indicator" v-if="merchSortBy === 'link'">{{ merchSortDirection === 'asc' ? '↑' : '↓' }}</span>
                                </span>
                                <span class="col-actions">ACTIONS</span>
                            </div>
                            <div class="merch-row" v-for="item in sortedMerch" :key="item.id">
                                <span class="col-image">
                                    <div class="merch-preview">
                                        <img v-if="item.imageLink" :src="item.imageLink" :alt="item.name" />
                                        <div v-else class="no-image">NO IMG</div>
                                    </div>
                                </span>
                                <span class="col-name">{{ item.name }}</span>
                                <span class="col-status" :class="{ available: item.canPurchase }">
                                    {{ item.canPurchase ? 'AVAILABLE' : 'SOLD OUT' }}
                                </span>
                                <span class="col-link">
                                    <a v-if="item.link" :href="item.link" target="_blank">VIEW</a>
                                    <span v-else class="no-data">—</span>
                                </span>
                                <span class="col-actions">
                                    <button class="edit-btn" @click="editMerch(item)">EDIT</button>
                                    <button class="delete-btn" @click="deleteMerch(item)">DELETE</button>
                                </span>
                            </div>
                        </template>
                    </div>
                </div>

                <!-- Tools Section (Super Admin Only) -->
                <div v-if="activeTab === 'tools' && isSuperAdmin" class="tools-section">
                    <div class="section-header">
                        <h2>DEVELOPER TOOLS</h2>
                    </div>

                    <div class="tools-list">
                        <div class="tool-card">
                            <h3>MIGRATE SHOW DATA</h3>
                            <p class="tool-description">Parse the date field to extract supporting acts. This will
                                analyze dates like
                                "January 15, 2026 w/ Band1, Band2" and move the bands to the support field.</p>
                            <div class="tool-actions">
                                <button class="tool-btn" @click="migrateShows" :disabled="migratingShows">
                                    {{ migratingShows ? 'MIGRATING...' : 'MIGRATE SUPPORT' }}
                                </button>
                                <button class="tool-btn" @click="normalizeShowsData" :disabled="normalizingShows">
                                    {{ normalizingShows ? 'NORMALIZING...' : 'NORMALIZE TIMES & CITY' }}
                                </button>
                            </div>
                        </div>

                        <div class="tool-card">
                            <h3>SEED DEFAULT DATA</h3>
                            <p class="tool-description">Upload the hardcoded default data to Firebase. This will add the
                                default merch
                                items and site content to Firestore.</p>
                            <div class="tool-actions">
                                <button class="tool-btn" @click="seedMerch" :disabled="seedingMerch">
                                    {{ seedingMerch ? 'UPLOADING...' : 'SEED MERCH' }}
                                </button>
                                <button class="tool-btn" @click="seedContent" :disabled="seedingContent">
                                    {{ seedingContent ? 'UPLOADING...' : 'SEED CONTENT' }}
                                </button>
                            </div>
                        </div>

                        <div class="tool-card">
                            <h3>DANGER ZONE</h3>
                            <p class="tool-description">Clear all data from a collection. This cannot be undone!</p>
                            <div class="tool-actions">
                                <button class="tool-btn danger" @click="clearCollection('shows')" :disabled="clearing">
                                    CLEAR SHOWS
                                </button>
                                <button class="tool-btn danger" @click="clearCollection('merch')" :disabled="clearing">
                                    CLEAR MERCH
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </main>

            <!-- Add/Edit Show Modal -->
            <div v-if="showModal" class="modal-overlay" @click.self="closeModal">
                <div class="modal modal-wide">
                    <h2>{{ editingShow ? 'EDIT SHOW' : 'ADD SHOW' }}</h2>
                    <div class="modal-form modal-form-grid">
                        <!-- Left Column -->
                        <div class="form-column">
                            <div class="form-field">
                                <label>START DATE <span class="required">*</span></label>
                                <input type="date" v-model="showForm.dateInput"
                                    :class="{ 'input-error': formErrors.date }" />
                                <p v-if="formErrors.date" class="field-error">{{ formErrors.date }}</p>
                            </div>

                            <div class="form-field">
                                <label>END DATE (optional)</label>
                                <input type="date" v-model="showForm.dateEndInput"
                                    :class="{ 'input-error': formErrors.dateEnd }" />
                                <p v-if="formErrors.dateEnd" class="field-error">{{ formErrors.dateEnd }}</p>
                                <p v-else class="field-hint">For multi-day events</p>
                            </div>

                            <div class="form-field">
                                <label>TIME <span class="required">*</span></label>
                                <input type="time" v-model="showForm.timeInput"
                                    :class="{ 'input-error': formErrors.time }" />
                                <p v-if="formErrors.time" class="field-error">{{ formErrors.time }}</p>
                            </div>

                            <div class="form-field">
                                <label>TICKETS URL (optional)</label>
                                <input type="url" v-model="showForm.tickets" placeholder="https://..."
                                    :class="{ 'input-error': formErrors.tickets }" />
                                <p v-if="formErrors.tickets" class="field-error">{{ formErrors.tickets }}</p>
                            </div>
                        </div>

                        <!-- Right Column -->
                        <div class="form-column">
                            <div class="form-field">
                                <label>VENUE <span class="required">*</span></label>
                                <input type="text" v-model="showForm.venue" placeholder="The Venue Name"
                                    :class="{ 'input-error': formErrors.venue }" />
                                <p v-if="formErrors.venue" class="field-error">{{ formErrors.venue }}</p>
                            </div>

                            <div class="form-field">
                                <label>CITY <span class="required">*</span></label>
                                <input type="text" v-model="showForm.city" placeholder="City, ST"
                                    :class="{ 'input-error': formErrors.city }" />
                                <p v-if="formErrors.city" class="field-error">{{ formErrors.city }}</p>
                            </div>

                            <div class="form-field">
                                <label>EVENT NAME (optional)</label>
                                <input type="text" v-model="showForm.event"
                                    placeholder="Festival Name, Tour Name, etc." />
                                <p class="field-hint">E.g., "epic summer tour"</p>
                            </div>

                            <div class="form-field">
                                <label>SUPPORT / OTHER ACTS (optional)</label>
                                <input type="text" v-model="showForm.support" placeholder="Band 1, Band 2, Band 3" />
                                <p class="field-hint">Comma-separated list</p>
                            </div>
                        </div>

                        <!-- Actions span both columns -->
                        <div class="modal-actions modal-actions-full">
                            <button class="cancel-btn" @click="closeModal">CANCEL</button>
                            <button class="save-btn" @click="saveShow" :disabled="savingShow">
                                {{ savingShow ? 'SAVING...' : 'SAVE' }}
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Add/Edit Merch Modal -->
            <div v-if="merchModal" class="modal-overlay" @click.self="closeMerchModal">
                <div class="modal">
                    <h2>{{ editingMerch ? 'EDIT ITEM' : 'ADD ITEM' }}</h2>
                    <div class="modal-form">
                        <label>NAME <span class="required">*</span></label>
                        <input type="text" v-model="merchForm.name" placeholder="LOGO T-SHIRT"
                            :class="{ 'input-error': merchFormErrors.name }" />
                        <p v-if="merchFormErrors.name" class="field-error">{{ merchFormErrors.name }}</p>

                        <label>IMAGE <span class="required">*</span></label>
                        <div class="image-upload-container">
                            <div class="image-preview" v-if="merchForm.imageLink || merchImagePreview">
                                <img :src="merchImagePreview || merchForm.imageLink" alt="Preview" />
                            </div>
                            <div class="image-upload-actions">
                                <label class="upload-btn">
                                    <input type="file" accept="image/*" @change="handleMerchImageUpload"
                                        ref="merchImageInput" />
                                    {{ merchForm.imageLink ? 'CHANGE IMAGE' : 'UPLOAD IMAGE' }}
                                </label>
                                <span v-if="uploadingMerchImage" class="upload-status">Uploading...</span>
                            </div>
                        </div>
                        <p v-if="merchFormErrors.imageLink" class="field-error">{{ merchFormErrors.imageLink }}</p>

                        <label>PURCHASE LINK (optional)</label>
                        <input type="url" v-model="merchForm.link" placeholder="https://bandcamp.com/..."
                            :class="{ 'input-error': merchFormErrors.link }" />
                        <p v-if="merchFormErrors.link" class="field-error">{{ merchFormErrors.link }}</p>

                        <label class="checkbox-label">
                            <input type="checkbox" v-model="merchForm.canPurchase" />
                            AVAILABLE FOR PURCHASE
                        </label>

                        <div class="modal-actions">
                            <button class="cancel-btn" @click="closeMerchModal">CANCEL</button>
                            <button class="save-btn" @click="saveMerch" :disabled="savingMerch || uploadingMerchImage">
                                {{ savingMerch ? 'SAVING...' : 'SAVE' }}
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { auth, db, storage } from '../firebase'
import { signInWithEmailAndPassword, signOut, onAuthStateChanged } from 'firebase/auth'
import { collection, getDocs, addDoc, updateDoc, deleteDoc, doc, getDoc, setDoc } from 'firebase/firestore'
import { ref as storageRef, uploadBytes, getDownloadURL } from 'firebase/storage'

export default {
    name: 'AdminPage',
    data() {
        return {
            // Auth
            user: null,
            email: '',
            password: '',
            error: '',
            loading: false,

            // Dashboard
            activeTab: 'shows',

            // Shows
            shows: [],
            loadingShows: false,
            showModal: false,
            editingShow: null,
            savingShow: false,
            showsSortBy: 'date',
            showsSortDirection: 'desc',
            showForm: {
                dateInput: '',      // YYYY-MM-DD format for input (start date)
                dateEndInput: '',   // YYYY-MM-DD format for input (end date, optional)
                timeInput: '',      // HH:MM format for input
                venue: '',
                city: '',
                tickets: '',
                support: '',        // Supporting acts (optional)
                event: ''           // Event name (optional)
            },
            formErrors: {
                date: '',
                dateEnd: '',
                time: '',
                venue: '',
                city: '',
                tickets: '',
                support: '',
                event: ''
            },

            // Content
            loadingContent: false,
            savingContent: false,
            contentForm: {
                videoTitle: '',
                videoUrl: '',
                playlistTitle: '',
                playlistUrl: '',
                musicImageUrl: ''
            },
            originalContent: {
                videoTitle: '',
                videoUrl: '',
                playlistTitle: '',
                playlistUrl: '',
                musicImageUrl: ''
            },

            // Merch
            merch: [],
            loadingMerch: false,
            merchModal: false,
            editingMerch: null,
            savingMerch: false,
            uploadingMerchImage: false,
            merchImagePreview: null,
            merchSortBy: 'name',
            merchSortDirection: 'asc',
            merchForm: {
                name: '',
                imageLink: '',
                link: '',
                canPurchase: true
            },
            merchFormErrors: {
                name: '',
                imageLink: '',
                link: ''
            },

            // Music Image Upload
            uploadingMusicImage: false,
            musicImagePreview: null,

            // Tools
            seedingMerch: false,
            seedingContent: false,
            migratingShows: false,
            normalizingShows: false,
            clearing: false
        }
    },
    computed: {
        isSuperAdmin() {
            return this.user && this.user.email === 'admin@coffeesyn.com'
        },
        hasContentChanges() {
            return (
                this.contentForm.videoTitle !== this.originalContent.videoTitle ||
                this.contentForm.videoUrl !== this.originalContent.videoUrl ||
                this.contentForm.playlistTitle !== this.originalContent.playlistTitle ||
                this.contentForm.playlistUrl !== this.originalContent.playlistUrl ||
                this.contentForm.musicImageUrl !== this.originalContent.musicImageUrl
            )
        },
        sortedShows() {
            if (!this.shows || this.shows.length === 0) return []

            const sorted = [...this.shows].sort((a, b) => {
                let aVal, bVal

                switch (this.showsSortBy) {
                    case 'date':
                        aVal = this.parseShowDateForSort(a.date)
                        bVal = this.parseShowDateForSort(b.date)
                        break
                    case 'time':
                        aVal = this.parseTimeForSort(a.time)
                        bVal = this.parseTimeForSort(b.time)
                        break
                    case 'venue':
                        aVal = (a.venue || '').toLowerCase()
                        bVal = (b.venue || '').toLowerCase()
                        break
                    case 'city':
                        aVal = (a.city || '').toLowerCase()
                        bVal = (b.city || '').toLowerCase()
                        break
                    case 'event':
                        aVal = (a.event || '').toLowerCase()
                        bVal = (b.event || '').toLowerCase()
                        break
                    case 'support':
                        aVal = (a.support || '').toLowerCase()
                        bVal = (b.support || '').toLowerCase()
                        break
                    default:
                        aVal = a[this.showsSortBy] || ''
                        bVal = b[this.showsSortBy] || ''
                }

                if (aVal < bVal) return this.showsSortDirection === 'asc' ? -1 : 1
                if (aVal > bVal) return this.showsSortDirection === 'asc' ? 1 : -1
                return 0
            })

            return sorted
        },
        sortedMerch() {
            if (!this.merch || this.merch.length === 0) return []

            const sorted = [...this.merch].sort((a, b) => {
                let aVal, bVal

                switch (this.merchSortBy) {
                    case 'name':
                        aVal = (a.name || '').toLowerCase()
                        bVal = (b.name || '').toLowerCase()
                        break
                    case 'status':
                        // Available items first when ascending
                        aVal = a.canPurchase ? 0 : 1
                        bVal = b.canPurchase ? 0 : 1
                        break
                    case 'link':
                        aVal = a.link ? 0 : 1
                        bVal = b.link ? 0 : 1
                        break
                    default:
                        aVal = a[this.merchSortBy] || ''
                        bVal = b[this.merchSortBy] || ''
                }

                if (aVal < bVal) return this.merchSortDirection === 'asc' ? -1 : 1
                if (aVal > bVal) return this.merchSortDirection === 'asc' ? 1 : -1
                return 0
            })

            return sorted
        }
    },
    watch: {
        showModal(isOpen) {
            this.toggleBodyScroll(isOpen)
        },
        merchModal(isOpen) {
            this.toggleBodyScroll(isOpen)
        }
    },
    mounted() {
        // Listen for auth state changes
        onAuthStateChanged(auth, (user) => {
            this.user = user
            if (user) {
                this.fetchContent()
                this.fetchShows()
                this.fetchMerch()
            }
        })
    },
    beforeUnmount() {
        // Ensure scroll is re-enabled when component unmounts
        document.body.style.overflow = ''
    },
    methods: {
        toggleBodyScroll(disableScroll) {
            if (disableScroll) {
                document.body.style.overflow = 'hidden'
            } else {
                document.body.style.overflow = ''
            }
        },

        async login() {
            if (!this.email || !this.password) {
                this.error = 'Please enter email and password'
                return
            }

            this.loading = true
            this.error = ''

            try {
                await signInWithEmailAndPassword(auth, this.email, this.password)
                this.email = ''
                this.password = ''
            } catch (err) {
                console.error('Login error:', err)
                if (err.code === 'auth/user-not-found' || err.code === 'auth/wrong-password') {
                    this.error = 'Invalid email or password'
                } else if (err.code === 'auth/invalid-email') {
                    this.error = 'Invalid email format'
                } else {
                    this.error = 'Login failed. Please try again.'
                }
            } finally {
                this.loading = false
            }
        },

        async logout() {
            try {
                await signOut(auth)
            } catch (err) {
                console.error('Logout error:', err)
            }
        },

        async fetchShows() {
            this.loadingShows = true
            try {
                const showsRef = collection(db, 'shows')
                const snapshot = await getDocs(showsRef)
                this.shows = snapshot.docs.map(doc => ({
                    id: doc.id,
                    ...doc.data()
                }))
            } catch (err) {
                console.error('Error fetching shows:', err)
            } finally {
                this.loadingShows = false
            }
        },

        sortShowsBy(column) {
            if (this.showsSortBy === column) {
                // Toggle direction
                this.showsSortDirection = this.showsSortDirection === 'asc' ? 'desc' : 'asc'
            } else {
                this.showsSortBy = column
                this.showsSortDirection = 'asc'
            }
        },

        sortMerchBy(column) {
            if (this.merchSortBy === column) {
                // Toggle direction
                this.merchSortDirection = this.merchSortDirection === 'asc' ? 'desc' : 'asc'
            } else {
                this.merchSortBy = column
                this.merchSortDirection = 'asc'
            }
        },

        parseShowDateForSort(dateString) {
            if (!dateString) return 0

            // Handle date ranges - take first date
            if (dateString.includes(' - ')) {
                dateString = dateString.split(' - ')[0]
            }

            // Remove ordinal suffixes
            dateString = dateString.replace(/(\d+)(st|nd|rd|th)/gi, '$1')

            const date = new Date(dateString)
            return isNaN(date.getTime()) ? 0 : date.getTime()
        },

        parseTimeForSort(timeString) {
            if (!timeString) return 0

            // Parse "8:00 PM" format
            const match = timeString.match(/(\d+):(\d+)\s*(AM|PM)/i)
            if (match) {
                let hours = parseInt(match[1], 10)
                const minutes = parseInt(match[2], 10)
                const ampm = match[3].toUpperCase()

                if (ampm === 'PM' && hours !== 12) hours += 12
                if (ampm === 'AM' && hours === 12) hours = 0

                return hours * 60 + minutes
            }

            return 0
        },

        async fetchContent() {
            this.loadingContent = true
            this.musicImagePreview = null
            try {
                const docRef = doc(db, 'siteContent', 'main')
                const docSnap = await getDoc(docRef)

                if (docSnap.exists()) {
                    const data = docSnap.data()
                    this.contentForm = {
                        videoTitle: data.videoTitle || '',
                        videoUrl: data.videoUrl || '',
                        playlistTitle: data.playlistTitle || '',
                        playlistUrl: data.playlistUrl || '',
                        musicImageUrl: data.musicImageUrl || ''
                    }
                    // Store original values for change detection
                    this.originalContent = { ...this.contentForm }
                }
            } catch (err) {
                console.error('Error fetching content:', err)
            } finally {
                this.loadingContent = false
            }
        },

        convertYouTubeUrl(url) {
            if (!url) return ''

            // Already an embed URL
            if (url.includes('/embed/')) {
                return url
            }

            // Extract video ID from various YouTube URL formats
            let videoId = null

            // youtu.be/VIDEO_ID
            const shortMatch = url.match(/youtu\.be\/([^?&]+)/)
            if (shortMatch) {
                videoId = shortMatch[1]
            }

            // youtube.com/watch?v=VIDEO_ID
            const watchMatch = url.match(/[?&]v=([^&]+)/)
            if (watchMatch) {
                videoId = watchMatch[1]
            }

            if (videoId) {
                return `https://www.youtube.com/embed/${videoId}`
            }

            return url
        },

        async saveContent() {
            this.savingContent = true
            try {
                const docRef = doc(db, 'siteContent', 'main')

                // Convert YouTube URL to embed format
                const videoUrl = this.convertYouTubeUrl(this.contentForm.videoUrl)

                // Update the form with the converted URL
                this.contentForm.videoUrl = videoUrl

                await setDoc(docRef, {
                    videoTitle: this.contentForm.videoTitle.trim(),
                    videoUrl: videoUrl,
                    playlistTitle: this.contentForm.playlistTitle.trim(),
                    playlistUrl: this.contentForm.playlistUrl.trim(),
                    musicImageUrl: this.contentForm.musicImageUrl.trim()
                })

                // Update original content to match saved content
                this.originalContent = { ...this.contentForm }

                alert('Content saved successfully!')
            } catch (err) {
                console.error('Error saving content:', err)
                alert('Failed to save content. Please try again.')
            } finally {
                this.savingContent = false
            }
        },

        // Image Upload Methods
        async uploadImage(file, folder) {
            const timestamp = Date.now()
            const safeName = file.name.replace(/[^a-zA-Z0-9.-]/g, '_')
            const fileName = `${folder}/${timestamp}_${safeName}`
            const fileRef = storageRef(storage, fileName)

            await uploadBytes(fileRef, file)
            const downloadUrl = await getDownloadURL(fileRef)
            return downloadUrl
        },

        async handleMerchImageUpload(event) {
            const file = event.target.files[0]
            if (!file) return

            // Show preview immediately
            this.merchImagePreview = URL.createObjectURL(file)
            this.uploadingMerchImage = true

            try {
                const downloadUrl = await this.uploadImage(file, 'merch')
                this.merchForm.imageLink = downloadUrl
                this.merchFormErrors.imageLink = ''
            } catch (err) {
                console.error('Error uploading merch image:', err)
                this.merchFormErrors.imageLink = 'Failed to upload image. Please try again.'
                this.merchImagePreview = null
            } finally {
                this.uploadingMerchImage = false
            }
        },

        async handleMusicImageUpload(event) {
            const file = event.target.files[0]
            if (!file) return

            // Show preview immediately
            this.musicImagePreview = URL.createObjectURL(file)
            this.uploadingMusicImage = true

            try {
                const downloadUrl = await this.uploadImage(file, 'content')
                this.contentForm.musicImageUrl = downloadUrl
            } catch (err) {
                console.error('Error uploading music image:', err)
                alert('Failed to upload image. Please try again.')
                this.musicImagePreview = null
            } finally {
                this.uploadingMusicImage = false
            }
        },

        removeMusicImage() {
            this.contentForm.musicImageUrl = ''
            this.musicImagePreview = null
            if (this.$refs.musicImageInput) {
                this.$refs.musicImageInput.value = ''
            }
        },

        // Merch Methods
        async fetchMerch() {
            this.loadingMerch = true
            try {
                const merchRef = collection(db, 'merch')
                const snapshot = await getDocs(merchRef)
                this.merch = snapshot.docs.map(doc => ({
                    id: doc.id,
                    ...doc.data()
                }))
            } catch (err) {
                console.error('Error fetching merch:', err)
            } finally {
                this.loadingMerch = false
            }
        },

        openAddMerchModal() {
            this.editingMerch = null
            this.merchImagePreview = null
            this.merchForm = {
                name: '',
                imageLink: '',
                link: '',
                canPurchase: true
            }
            this.clearMerchFormErrors()
            this.merchModal = true
        },

        editMerch(item) {
            this.editingMerch = item
            this.merchImagePreview = null
            this.merchForm = {
                name: item.name || '',
                imageLink: item.imageLink || '',
                link: item.link || '',
                canPurchase: item.canPurchase !== false
            }
            this.clearMerchFormErrors()
            this.merchModal = true
        },

        closeMerchModal() {
            this.merchModal = false
            this.editingMerch = null
            this.merchImagePreview = null
            this.clearMerchFormErrors()
        },

        clearMerchFormErrors() {
            this.merchFormErrors = {
                name: '',
                imageLink: '',
                link: ''
            }
        },

        validateMerchForm() {
            this.clearMerchFormErrors()
            let isValid = true

            if (!this.merchForm.name || this.merchForm.name.trim() === '') {
                this.merchFormErrors.name = 'Name is required'
                isValid = false
            }

            if (!this.merchForm.imageLink || this.merchForm.imageLink.trim() === '') {
                this.merchFormErrors.imageLink = 'Image URL is required'
                isValid = false
            }

            // Link is optional but must be valid URL if provided
            if (this.merchForm.link && this.merchForm.link.trim() !== '') {
                try {
                    new URL(this.merchForm.link)
                } catch {
                    this.merchFormErrors.link = 'Please enter a valid URL'
                    isValid = false
                }
            }

            return isValid
        },

        async saveMerch() {
            if (!this.validateMerchForm()) {
                return
            }

            this.savingMerch = true

            try {
                const merchData = {
                    name: this.merchForm.name.trim(),
                    imageLink: this.merchForm.imageLink.trim(),
                    link: this.merchForm.link.trim(),
                    canPurchase: this.merchForm.canPurchase
                }

                if (this.editingMerch) {
                    const merchRef = doc(db, 'merch', this.editingMerch.id)
                    await updateDoc(merchRef, merchData)
                } else {
                    await addDoc(collection(db, 'merch'), merchData)
                }

                this.closeMerchModal()
                await this.fetchMerch()
            } catch (err) {
                console.error('Error saving merch:', err)
                alert('Failed to save item. Please try again.')
            } finally {
                this.savingMerch = false
            }
        },

        async deleteMerch(item) {
            if (!confirm(`Delete "${item.name}"?`)) {
                return
            }

            try {
                await deleteDoc(doc(db, 'merch', item.id))
                await this.fetchMerch()
            } catch (err) {
                console.error('Error deleting merch:', err)
                alert('Failed to delete item. Please try again.')
            }
        },

        // Migration Methods
        async migrateShows() {
            if (!confirm('This will analyze all shows and extract supporting acts from the date field. Continue?')) {
                return
            }

            this.migratingShows = true
            let updatedCount = 0

            try {
                const showsRef = collection(db, 'shows')
                const snapshot = await getDocs(showsRef)

                for (const showDoc of snapshot.docs) {
                    const show = showDoc.data()
                    const dateField = show.date || ''

                    // Skip if already has support or date looks clean
                    if (show.support && show.support.trim() !== '') {
                        continue
                    }

                    // Common patterns to look for:
                    // "January 15, 2026 w/ Band1, Band2"
                    // "January 15, 2026 with Band1, Band2"
                    // "January 15, 2026 - Band1, Band2"
                    // "January 15, 2026 (Band1, Band2)"
                    // "January 15, 2026 // Band1, Band2"
                    // "January 15, 2026 | Band1, Band2"

                    let cleanDate = dateField
                    let support = ''

                    // Pattern: w/ or with
                    const withMatch = dateField.match(/^(.+?)\s*(?:w\/|with)\s*(.+)$/i)
                    if (withMatch) {
                        cleanDate = withMatch[1].trim()
                        support = withMatch[2].trim()
                    }

                    // Pattern: dash separator (but not date ranges like "Jan 15 - 17")
                    if (!support) {
                        const dashMatch = dateField.match(/^(.+?,\s*\d{4})\s*[-–—]\s*(.+)$/i)
                        if (dashMatch) {
                            cleanDate = dashMatch[1].trim()
                            support = dashMatch[2].trim()
                        }
                    }

                    // Pattern: parentheses
                    if (!support) {
                        const parenMatch = dateField.match(/^(.+?)\s*\((.+?)\)\s*$/)
                        if (parenMatch) {
                            cleanDate = parenMatch[1].trim()
                            support = parenMatch[2].trim()
                        }
                    }

                    // Pattern: double slash or pipe
                    if (!support) {
                        const slashMatch = dateField.match(/^(.+?)\s*(?:\/\/|\|)\s*(.+)$/i)
                        if (slashMatch) {
                            cleanDate = slashMatch[1].trim()
                            support = slashMatch[2].trim()
                        }
                    }

                    // Pattern: asterisk
                    if (!support) {
                        const asteriskMatch = dateField.match(/^(.+?)\s*\*\s*(.+)$/i)
                        if (asteriskMatch) {
                            cleanDate = asteriskMatch[1].trim()
                            support = asteriskMatch[2].trim()
                        }
                    }

                    // If we found something to extract, update the document
                    if (support) {
                        const showRef = doc(db, 'shows', showDoc.id)
                        await updateDoc(showRef, {
                            date: cleanDate,
                            support: support
                        })
                        updatedCount++
                        console.log(`Migrated show: "${dateField}" -> date: "${cleanDate}", support: "${support}"`)
                    }
                }

                if (updatedCount > 0) {
                    alert(`Successfully migrated ${updatedCount} shows!`)
                    await this.fetchShows()
                } else {
                    alert('No shows needed migration. All dates appear to be clean.')
                }
            } catch (err) {
                console.error('Error migrating shows:', err)
                alert('Failed to migrate shows. Check console for details.')
            } finally {
                this.migratingShows = false
            }
        },

        async normalizeShowsData() {
            if (!confirm('This will normalize all show times to "8:00 PM" format and set city to "Salt Lake City, UT". Continue?')) {
                return
            }

            this.normalizingShows = true
            let updatedCount = 0

            try {
                const showsRef = collection(db, 'shows')
                const snapshot = await getDocs(showsRef)

                for (const showDoc of snapshot.docs) {
                    const show = showDoc.data()
                    const updates = {}

                    // Normalize time
                    const timeField = show.time || ''
                    const normalizedTime = this.normalizeTime(timeField)
                    if (normalizedTime && normalizedTime !== timeField) {
                        updates.time = normalizedTime
                    }

                    // Set city to Salt Lake City, UT
                    if (!show.city || show.city.trim() === '' || !show.city.includes('Salt Lake')) {
                        updates.city = 'Salt Lake City, UT'
                    }

                    // Update if there are changes
                    if (Object.keys(updates).length > 0) {
                        const showRef = doc(db, 'shows', showDoc.id)
                        await updateDoc(showRef, updates)
                        updatedCount++
                        console.log(`Updated show at ${show.venue}:`, updates)
                    }
                }

                if (updatedCount > 0) {
                    alert(`Successfully updated ${updatedCount} shows!`)
                    await this.fetchShows()
                } else {
                    alert('No shows needed updates.')
                }
            } catch (err) {
                console.error('Error normalizing shows:', err)
                alert('Failed to normalize shows. Check console for details.')
            } finally {
                this.normalizingShows = false
            }
        },

        normalizeTime(timeStr) {
            if (!timeStr) return '8:00 PM'

            timeStr = timeStr.trim().toUpperCase()

            // Already in good format like "8:00 PM"
            if (/^\d{1,2}:\d{2}\s*(AM|PM)$/i.test(timeStr)) {
                // Just clean it up
                const match = timeStr.match(/^(\d{1,2}):(\d{2})\s*(AM|PM)$/i)
                if (match) {
                    return `${match[1]}:${match[2]} ${match[3].toUpperCase()}`
                }
            }

            // Format: "8PM" or "8 PM"
            const simpleMatch = timeStr.match(/^(\d{1,2})\s*(AM|PM)$/i)
            if (simpleMatch) {
                return `${simpleMatch[1]}:00 ${simpleMatch[2].toUpperCase()}`
            }

            // Format: "8:00pm" (no space)
            const noSpaceMatch = timeStr.match(/^(\d{1,2}):(\d{2})(AM|PM)$/i)
            if (noSpaceMatch) {
                return `${noSpaceMatch[1]}:${noSpaceMatch[2]} ${noSpaceMatch[3].toUpperCase()}`
            }

            // Format: "20:00" (24-hour)
            const militaryMatch = timeStr.match(/^(\d{1,2}):(\d{2})$/)
            if (militaryMatch) {
                let hours = parseInt(militaryMatch[1], 10)
                const minutes = militaryMatch[2]
                const ampm = hours >= 12 ? 'PM' : 'AM'
                if (hours > 12) hours -= 12
                if (hours === 0) hours = 12
                return `${hours}:${minutes} ${ampm}`
            }

            // Format: Just a number like "8" or "9"
            const justNumberMatch = timeStr.match(/^(\d{1,2})$/)
            if (justNumberMatch) {
                const hour = parseInt(justNumberMatch[1], 10)
                // Assume PM for typical show times (6-11)
                const ampm = hour >= 6 && hour <= 11 ? 'PM' : (hour < 6 ? 'PM' : 'AM')
                return `${hour}:00 ${ampm}`
            }

            // Couldn't parse, return default
            return '8:00 PM'
        },

        // Seed Methods
        async seedMerch() {
            if (!confirm('This will add default merch items to Firebase. Continue?')) {
                return
            }

            this.seedingMerch = true

            const defaultMerch = [
                {
                    name: "LOGO T-SHIRT",
                    link: "https://nadezhda.bandcamp.com/merch/logo-t-shirt",
                    imageLink: "../assets/graphics/merch/NDZ_T.png",
                    canPurchase: true
                },
                {
                    name: "ONE OF US T-SHIRT",
                    link: "https://nadezhda.bandcamp.com/merch/nadezhda-one-of-us-t-shirt",
                    imageLink: "../assets/graphics/merch/NDZ_OneOfUs.png",
                    canPurchase: true
                },
                {
                    name: "ORANGE TOTE",
                    link: "https://nadezhda.bandcamp.com/merch/nadezhda-orange-tote",
                    imageLink: "../assets/graphics/merch/NDZ_Tote.png",
                    canPurchase: true
                },
                {
                    name: "IRON ON PATCH",
                    link: "https://nadezhda.bandcamp.com/merch/nadezhda-goodside-patch",
                    imageLink: "../assets/graphics/merch/NDZ_Patch.png",
                    canPurchase: true
                },
                {
                    name: "NADDY BIC LIGHTER",
                    link: "",
                    imageLink: "../assets/graphics/merch/NDZ_Bic.png",
                    canPurchase: false
                }
            ]

            try {
                for (const item of defaultMerch) {
                    await addDoc(collection(db, 'merch'), item)
                }
                alert(`Successfully added ${defaultMerch.length} merch items!`)
                await this.fetchMerch()
            } catch (err) {
                console.error('Error seeding merch:', err)
                alert('Failed to seed merch. Please try again.')
            } finally {
                this.seedingMerch = false
            }
        },

        async seedContent() {
            if (!confirm('This will set the default site content in Firebase. Continue?')) {
                return
            }

            this.seedingContent = true

            const defaultContent = {
                videoTitle: 'BUSY BEING BORED VIDEO OUT NOW',
                videoUrl: 'https://www.youtube.com/embed/r78IdK2ycYY',
                playlistTitle: 'BATTERY EP OUT NOW',
                playlistUrl: 'https://open.spotify.com/embed/album/60qvcz15u7N34yDKf7Icdf?utm_source=generator',
                musicImageUrl: ''
            }

            try {
                const docRef = doc(db, 'siteContent', 'main')
                await setDoc(docRef, defaultContent)
                alert('Successfully set default content!')
                await this.fetchContent()
            } catch (err) {
                console.error('Error seeding content:', err)
                alert('Failed to seed content. Please try again.')
            } finally {
                this.seedingContent = false
            }
        },

        async clearCollection(collectionName) {
            if (!confirm(`WARNING: This will delete ALL ${collectionName}. This cannot be undone! Are you sure?`)) {
                return
            }

            if (!confirm(`FINAL WARNING: You are about to delete all ${collectionName}. Type "yes" mentality and click OK to proceed.`)) {
                return
            }

            this.clearing = true

            try {
                const collRef = collection(db, collectionName)
                const snapshot = await getDocs(collRef)

                const deletePromises = snapshot.docs.map(doc => deleteDoc(doc.ref))
                await Promise.all(deletePromises)

                alert(`Successfully cleared ${snapshot.docs.length} items from ${collectionName}!`)

                if (collectionName === 'shows') {
                    await this.fetchShows()
                } else if (collectionName === 'merch') {
                    await this.fetchMerch()
                }
            } catch (err) {
                console.error(`Error clearing ${collectionName}:`, err)
                alert(`Failed to clear ${collectionName}. Please try again.`)
            } finally {
                this.clearing = false
            }
        },

        openAddShowModal() {
            this.editingShow = null
            this.showForm = {
                dateInput: '',
                dateEndInput: '',
                timeInput: '',
                venue: '',
                city: '',
                tickets: '',
                support: '',
                event: ''
            }
            this.clearFormErrors()
            this.showModal = true
        },

        editShow(show) {
            this.editingShow = show
            // Parse the date - could be single date or range
            const { startDate, endDate } = this.parseDisplayDateRange(show.date)
            this.showForm = {
                dateInput: startDate || '',
                dateEndInput: endDate || '',
                timeInput: this.parseDisplayTimeToInput(show.time) || '',
                venue: show.venue || '',
                city: show.city || '',
                tickets: show.tickets || '',
                support: show.support || '',
                event: show.event || ''
            }
            this.clearFormErrors()
            this.showModal = true
        },

        closeModal() {
            this.showModal = false
            this.editingShow = null
            this.clearFormErrors()
        },

        clearFormErrors() {
            this.formErrors = {
                date: '',
                dateEnd: '',
                time: '',
                venue: '',
                city: '',
                tickets: '',
                support: '',
                event: ''
            }
        },

        validateForm() {
            this.clearFormErrors()
            let isValid = true

            // Date validation
            if (!this.showForm.dateInput) {
                this.formErrors.date = 'Start date is required'
                isValid = false
            }

            // End date validation (optional but must be after start date if provided)
            if (this.showForm.dateEndInput && this.showForm.dateInput) {
                const startDate = new Date(this.showForm.dateInput)
                const endDate = new Date(this.showForm.dateEndInput)
                if (endDate < startDate) {
                    this.formErrors.dateEnd = 'End date must be after start date'
                    isValid = false
                }
            }

            // Time validation
            if (!this.showForm.timeInput) {
                this.formErrors.time = 'Time is required'
                isValid = false
            }

            // Venue validation
            if (!this.showForm.venue || this.showForm.venue.trim() === '') {
                this.formErrors.venue = 'Venue is required'
                isValid = false
            }

            // City validation
            if (!this.showForm.city || this.showForm.city.trim() === '') {
                this.formErrors.city = 'City is required'
                isValid = false
            }

            // Tickets URL validation (optional but must be valid if provided)
            if (this.showForm.tickets && this.showForm.tickets.trim() !== '') {
                try {
                    new URL(this.showForm.tickets)
                } catch {
                    this.formErrors.tickets = 'Please enter a valid URL (e.g., https://...)'
                    isValid = false
                }
            }

            return isValid
        },

        // Convert YYYY-MM-DD to display format "January 15, 2026"
        // If endDateInput provided, formats as "January 15 - 17, 2026" or "January 15 - February 2, 2026"
        formatDateForDisplay(dateInput, endDateInput = '') {
            if (!dateInput) return ''
            const startDate = new Date(dateInput + 'T00:00:00')

            if (!endDateInput) {
                // Single date
                return startDate.toLocaleDateString('en-US', {
                    month: 'long',
                    day: 'numeric',
                    year: 'numeric'
                })
            }

            const endDate = new Date(endDateInput + 'T00:00:00')
            const sameMonth = startDate.getMonth() === endDate.getMonth()
            const sameYear = startDate.getFullYear() === endDate.getFullYear()

            if (sameMonth && sameYear) {
                // Same month: "January 15 - 17, 2026"
                const month = startDate.toLocaleDateString('en-US', { month: 'long' })
                const startDay = startDate.getDate()
                const endDay = endDate.getDate()
                const year = startDate.getFullYear()
                return `${month} ${startDay} - ${endDay}, ${year}`
            } else if (sameYear) {
                // Different month, same year: "January 15 - February 2, 2026"
                const startStr = startDate.toLocaleDateString('en-US', { month: 'long', day: 'numeric' })
                const endStr = endDate.toLocaleDateString('en-US', { month: 'long', day: 'numeric' })
                const year = startDate.getFullYear()
                return `${startStr} - ${endStr}, ${year}`
            } else {
                // Different year: "December 30, 2025 - January 2, 2026"
                const startStr = startDate.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })
                const endStr = endDate.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })
                return `${startStr} - ${endStr}`
            }
        },

        // Convert HH:MM (24hr) to display format "8:00 PM"
        formatTimeForDisplay(timeInput) {
            if (!timeInput) return ''
            const [hours, minutes] = timeInput.split(':')
            const hour = parseInt(hours, 10)
            const ampm = hour >= 12 ? 'PM' : 'AM'
            const displayHour = hour % 12 || 12
            return `${displayHour}:${minutes} ${ampm}`
        },

        // Parse display date "January 15, 2026" back to YYYY-MM-DD
        parseDisplayDateToInput(displayDate) {
            if (!displayDate) return ''
            const date = new Date(displayDate)
            if (isNaN(date.getTime())) return ''
            return date.toISOString().split('T')[0]
        },

        // Parse display date range back to { startDate, endDate } in YYYY-MM-DD format
        parseDisplayDateRange(displayDate) {
            if (!displayDate) return { startDate: '', endDate: '' }

            // Check if it's a date range (contains " - ")
            if (!displayDate.includes(' - ')) {
                // Single date
                return { startDate: this.parseDisplayDateToInput(displayDate), endDate: '' }
            }

            const parts = displayDate.split(' - ')
            if (parts.length !== 2) {
                return { startDate: this.parseDisplayDateToInput(displayDate), endDate: '' }
            }

            // Try to parse different formats:
            // "January 15 - 17, 2026" (same month)
            // "January 15 - February 2, 2026" (different month, same year)
            // "December 30, 2025 - January 2, 2026" (different year)

            const startPart = parts[0].trim()
            const endPart = parts[1].trim()

            // Check if end part is just a day number (same month format)
            if (/^\d+,?\s*\d*$/.test(endPart)) {
                // "January 15 - 17, 2026" format
                const match = endPart.match(/(\d+),?\s*(\d*)/)
                if (match) {
                    const endDay = match[1]
                    const year = match[2] || new Date().getFullYear()
                    // Get month from start part
                    const startDate = new Date(startPart + ', ' + year)
                    if (!isNaN(startDate.getTime())) {
                        const month = startDate.toLocaleDateString('en-US', { month: 'long' })
                        const endDate = new Date(`${month} ${endDay}, ${year}`)
                        return {
                            startDate: startDate.toISOString().split('T')[0],
                            endDate: !isNaN(endDate.getTime()) ? endDate.toISOString().split('T')[0] : ''
                        }
                    }
                }
            }

            // Try parsing end part as a full date with year context
            let endDate = new Date(endPart)
            if (isNaN(endDate.getTime())) {
                // Try adding year from context
                const yearMatch = displayDate.match(/\d{4}/)
                if (yearMatch) {
                    endDate = new Date(endPart + ', ' + yearMatch[0])
                }
            }

            // Parse start date
            let startDate = new Date(startPart)
            if (isNaN(startDate.getTime())) {
                // Try adding year
                const yearMatch = displayDate.match(/\d{4}/)
                if (yearMatch) {
                    startDate = new Date(startPart + ', ' + yearMatch[0])
                }
            }

            return {
                startDate: !isNaN(startDate.getTime()) ? startDate.toISOString().split('T')[0] : '',
                endDate: !isNaN(endDate.getTime()) ? endDate.toISOString().split('T')[0] : ''
            }
        },

        // Parse display time "8:00 PM" back to HH:MM
        parseDisplayTimeToInput(displayTime) {
            if (!displayTime) return ''
            const match = displayTime.match(/(\d+):(\d+)\s*(AM|PM)/i)
            if (!match) return ''
            let hours = parseInt(match[1], 10)
            const minutes = match[2]
            const ampm = match[3].toUpperCase()
            if (ampm === 'PM' && hours !== 12) hours += 12
            if (ampm === 'AM' && hours === 12) hours = 0
            return `${hours.toString().padStart(2, '0')}:${minutes}`
        },

        async saveShow() {
            if (!this.validateForm()) {
                return
            }

            this.savingShow = true

            try {
                // Convert input formats to display formats for storage
                const showData = {
                    date: this.formatDateForDisplay(this.showForm.dateInput, this.showForm.dateEndInput),
                    time: this.formatTimeForDisplay(this.showForm.timeInput),
                    venue: this.showForm.venue.trim(),
                    city: this.showForm.city.trim(),
                    tickets: this.showForm.tickets.trim(),
                    support: this.showForm.support.trim(),
                    event: this.showForm.event.trim()
                }

                if (this.editingShow) {
                    // Update existing show
                    const showRef = doc(db, 'shows', this.editingShow.id)
                    await updateDoc(showRef, showData)
                } else {
                    // Add new show
                    await addDoc(collection(db, 'shows'), showData)
                }

                this.closeModal()
                await this.fetchShows()
            } catch (err) {
                console.error('Error saving show:', err)
                alert('Failed to save show. Please try again.')
            } finally {
                this.savingShow = false
            }
        },

        async deleteShow(show) {
            if (!confirm(`Delete show at ${show.venue} on ${show.date}?`)) {
                return
            }

            try {
                await deleteDoc(doc(db, 'shows', show.id))
                await this.fetchShows()
            } catch (err) {
                console.error('Error deleting show:', err)
                alert('Failed to delete show. Please try again.')
            }
        }
    }
}
</script>

<style scoped>
* {
    text-transform: uppercase;
}

.admin-page {
    min-height: 100vh;
    background: #f06825;
    color: black;
    text-transform: uppercase;
}

/* Login Styles */
.login-container {
    display: flex;
    justify-content: center;
    align-items: center;
    min-height: 100vh;
    padding: 20px;
}

.login-box {
    background: transparent;
    padding: 40px;
    width: 100%;
    max-width: 400px;
}

.login-box h1 {
    font-family: BNAxelGroteskBold, sans-serif;
    text-align: center;
    color: black;
    margin: 0 0 40px 0;
    font-size: 3rem;
    letter-spacing: 2px;
}

.login-form {
    display: flex;
    flex-direction: column;
}

.login-form label {
    font-family: BNAxelGroteskBold, sans-serif;
    font-size: 0.8rem;
    letter-spacing: 2px;
    margin-bottom: 8px;
    color: black;
}

.login-form input {
    font-family: NeueMontrealRegular, sans-serif;
    font-size: 1rem;
    padding: 12px 0;
    margin-bottom: 25px;
    background: transparent;
    border: none;
    border-bottom: 2px solid black;
    color: black;
    outline: none;
    transition: all 0.2s;
}

.login-form input:focus {
    border-bottom-color: #fcb416;
}

.login-form input::placeholder {
    color: rgba(0, 0, 0, 0.4);
}

.login-form button {
    font-family: BNAxelGroteskBold, sans-serif;
    font-size: 1.2rem;
    padding: 15px;
    background: transparent;
    color: black;
    border: none;
    cursor: pointer;
    letter-spacing: 2px;
    margin-top: 20px;
}

.login-form button:hover:not(:disabled) {
    color: #fcb416;
    animation: shake 1s infinite;
}

.login-form button:disabled {
    opacity: 0.5;
    cursor: not-allowed;
}

.error {
    color: black;
    font-family: NeueMontrealBold, sans-serif;
    font-size: 0.9rem;
    margin-bottom: 15px;
    text-align: center;
}

/* Dashboard Styles */
.admin-header {
    background: transparent;
    padding: 30px;
    display: flex;
    justify-content: space-between;
    align-items: center;
    border-bottom: 2px solid black;
}

.admin-header h1 {
    font-family: BNAxelGroteskBold, sans-serif;
    color: black;
    margin: 0;
    font-size: 1.8rem;
    letter-spacing: 2px;
}

.header-actions {
    display: flex;
    align-items: center;
    gap: 30px;
}

.user-email {
    font-family: NeueMontrealRegular, sans-serif;
    color: black;
    font-size: 0.9rem;
    opacity: 0.6;
}

.logout-btn {
    font-family: BNAxelGroteskBold, sans-serif;
    padding: 0;
    background: transparent;
    color: black;
    border: none;
    cursor: pointer;
    font-size: 1rem;
    letter-spacing: 1px;
}

.logout-btn {
    color: #fcb416;
}

.logout-btn:hover {
    color: black;
    animation: shake 1s infinite;
}

.admin-nav {
    background: transparent;
    padding: 20px 30px;
    display: flex;
    gap: 40px;
    border-bottom: 2px solid black;
}

.admin-nav button {
    font-family: BNAxelGroteskBold, sans-serif;
    padding: 0 0 5px 0;
    background: transparent;
    color: black;
    border: none;
    border-bottom: 3px solid transparent;
    cursor: pointer;
    font-size: 1.2rem;
    letter-spacing: 1px;
    transition: color 0.2s;
}

.admin-nav button:hover {
    color: #fcb416;
    animation: shake 1s infinite;
}

.admin-nav button.active {
    border-bottom-color: black;
}

.admin-content {
    padding: 40px 30px;
}

.section-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 30px;
}

.section-header h2 {
    font-family: BNAxelGroteskBold, sans-serif;
    color: black;
    margin: 0;
    font-size: 1.5rem;
    letter-spacing: 1px;
}

.add-btn {
    font-family: BNAxelGroteskBold, sans-serif;
    padding: 0;
    background: transparent;
    color: #fcb416;
    border: none;
    cursor: pointer;
    font-size: 1rem;
    letter-spacing: 1px;
}

.add-btn:hover:not(:disabled) {
    color: black;
    animation: shake 1s infinite;
}

.add-btn:disabled {
    opacity: 0.3;
    cursor: not-allowed;
}

.loading,
.empty,
.coming-soon {
    font-family: NeueMontrealRegular, sans-serif;
    color: black;
    text-align: center;
    padding: 60px;
    opacity: 0.5;
}

/* Shows Table */
.shows-table {
    width: 100%;
}

.shows-header {
    display: grid;
    grid-template-columns: 1.2fr 0.7fr 1.5fr 1fr 1.2fr 1.5fr 0.6fr 1fr;
    gap: 12px;
    padding: 15px 0;
    border-bottom: 2px solid black;
    font-family: BNAxelGroteskBold, sans-serif;
    font-size: 0.75rem;
    letter-spacing: 1px;
    color: black;
}

.shows-header .sortable {
    cursor: pointer;
    user-select: none;
    display: flex;
    align-items: center;
    gap: 5px;
}

.shows-header .sortable:hover {
    color: #fcb416;
}

.sort-indicator {
    font-size: 0.7rem;
}

.shows-row {
    display: grid;
    grid-template-columns: 1.2fr 0.7fr 1.5fr 1fr 1.2fr 1.5fr 0.6fr 1fr;
    gap: 12px;
    padding: 18px 0;
    border-bottom: 1px solid rgba(0, 0, 0, 0.15);
    align-items: center;
    font-family: NeueMontrealRegular, sans-serif;
    font-size: 0.9rem;
    color: black;
}

.shows-row:hover {
    background: rgba(252, 180, 22, 0.1);
}

/* Row-specific column styles (don't affect header) */
.shows-row .col-date {
    font-family: NeueMontrealBold, sans-serif;
}

.shows-row .col-time {
    opacity: 0.6;
}

.shows-row .col-event {
    font-size: 0.85rem;
}

.shows-row .col-support {
    font-size: 0.85rem;
    opacity: 0.7;
}

.col-tickets a {
    color: #fcb416;
    text-decoration: none;
    font-family: BNAxelGroteskBold, sans-serif;
    font-size: 0.8rem;
    letter-spacing: 1px;
}

.col-tickets a:hover {
    color: black;
    animation: shake 1s infinite;
}

.col-tickets .no-tickets {
    opacity: 0.3;
}

.no-data {
    opacity: 0.3;
}

.col-actions {
    display: flex;
    gap: 15px;
}

.edit-btn,
.delete-btn {
    font-family: BNAxelGroteskBold, sans-serif;
    padding: 0;
    border: none;
    background: transparent;
    cursor: pointer;
    font-size: 0.9rem;
    letter-spacing: 1px;
    color: #fcb416;
}

.edit-btn:hover,
.delete-btn:hover {
    color: black;
    animation: shake 1s infinite;
}

/* Modal Styles */
.modal-overlay {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(240, 104, 37, 0.95);
    display: flex;
    justify-content: center;
    align-items: flex-start;
    z-index: 1000;
    overflow-y: auto;
    padding: 20px 0;
}

.modal {
    background: #f06825;
    padding: 40px;
    width: 100%;
    max-width: 500px;
    margin: 20px;
    max-height: none;
}

.modal-wide {
    max-width: 800px;
}

.modal h2 {
    font-family: BNAxelGroteskBold, sans-serif;
    color: black;
    margin: 0 0 30px 0;
    font-size: 1.8rem;
    letter-spacing: 1px;
}

.modal-form {
    display: flex;
    flex-direction: column;
}

.modal-form-grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 30px;
}

.form-column {
    display: flex;
    flex-direction: column;
    gap: 5px;
}

.form-field {
    display: flex;
    flex-direction: column;
}

.form-field input {
    margin-bottom: 5px;
}

.modal-actions-full {
    grid-column: 1 / -1;
}

.modal-form label {
    font-family: BNAxelGroteskBold, sans-serif;
    font-size: 0.75rem;
    letter-spacing: 2px;
    margin-bottom: 8px;
    color: black;
}

.modal-form input {
    font-family: NeueMontrealRegular, sans-serif;
    font-size: 1rem;
    padding: 12px 0;
    margin-bottom: 20px;
    background: transparent;
    border: none;
    border-bottom: 2px solid black;
    color: black;
    outline: none;
}

.modal-form input:focus {
    border-bottom-color: #fcb416;
}

.modal-form input::placeholder {
    color: rgba(0, 0, 0, 0.4);
}

.modal-form .field-hint {
    font-family: NeueMontrealRegular, sans-serif;
    font-size: 0.75rem;
    color: #fcb416;
    margin: -15px 0 15px 0;
}

.modal-form input.input-error {
    border-bottom-color: #8B0000;
}

.modal-form .required {
    color: #fcb416;
}

.modal-form .field-error {
    color: black;
    font-family: NeueMontrealRegular, sans-serif;
    font-size: 0.8rem;
    margin: -15px 0 15px 0;
    opacity: 0.7;
}

.modal-form input[type="date"],
.modal-form input[type="time"] {
    color-scheme: light;
}

.modal-actions {
    display: flex;
    justify-content: flex-start;
    gap: 30px;
    margin-top: 20px;
}

.cancel-btn,
.save-btn {
    font-family: BNAxelGroteskBold, sans-serif;
    padding: 0;
    border: none;
    background: transparent;
    cursor: pointer;
    font-size: 1rem;
    letter-spacing: 1px;
    color: #fcb416;
}

.cancel-btn:hover {
    color: black;
    animation: shake 1s infinite;
}

.save-btn:hover:not(:disabled) {
    color: black;
    animation: shake 1s infinite;
}

.save-btn:disabled {
    opacity: 0.4;
    cursor: not-allowed;
}

/* Primary Save Button (more prominent) */
.save-btn-primary {
    background: #fcb416;
    color: black;
    padding: 15px 30px;
    font-size: 1.1rem;
}

.save-btn-primary:hover:not(:disabled) {
    background: black;
    color: #fcb416;
    animation: shake 1s infinite;
}

.save-btn-primary:disabled {
    background: rgba(252, 180, 22, 0.4);
    color: rgba(0, 0, 0, 0.4);
}

/* Content Section */
.content-form {
    display: flex;
    flex-direction: column;
    gap: 0;
    padding-bottom: 80px;
}

.content-card {
    background: transparent;
    padding: 30px 0;
    border-bottom: 1px solid rgba(0, 0, 0, 0.2);
}

.content-card h3 {
    font-family: BNAxelGroteskBold, sans-serif;
    color: black;
    margin: 0 0 25px 0;
    font-size: 1.1rem;
    letter-spacing: 1px;
}

.content-card label {
    font-family: BNAxelGroteskBold, sans-serif;
    font-size: 0.75rem;
    letter-spacing: 2px;
    margin-bottom: 8px;
    color: black;
    display: block;
}

.content-card input {
    font-family: NeueMontrealRegular, sans-serif;
    font-size: 1rem;
    padding: 12px 0;
    margin-bottom: 5px;
    background: transparent;
    border: none;
    border-bottom: 2px solid black;
    color: black;
    outline: none;
    width: 100%;
    box-sizing: border-box;
}

.content-card input:focus {
    border-bottom-color: #fcb416;
}

.content-card input::placeholder {
    color: rgba(0, 0, 0, 0.4);
}

.field-hint {
    font-family: NeueMontrealRegular, sans-serif;
    font-size: 0.75rem;
    color: #fcb416;
    margin: 5px 0 20px 0;
}

.content-actions {
    display: flex;
    justify-content: flex-start;
    position: fixed;
    bottom: 0;
    left: 0;
    right: 0;
    background: #f06825;
    padding: 20px 30px;
    border-top: 2px solid black;
    z-index: 100;
}

/* Merch Section */
.merch-table {
    width: 100%;
}

.merch-header {
    display: grid;
    grid-template-columns: 80px 2fr 1fr 1fr 1fr;
    gap: 15px;
    padding: 15px 0;
    border-bottom: 2px solid black;
    font-family: BNAxelGroteskBold, sans-serif;
    font-size: 0.75rem;
    letter-spacing: 1px;
    color: black;
    align-items: center;
}

.merch-header .sortable {
    cursor: pointer;
    user-select: none;
    display: flex;
    align-items: center;
    gap: 5px;
}

.merch-header .sortable:hover {
    color: #fcb416;
}

.merch-row {
    display: grid;
    grid-template-columns: 80px 2fr 1fr 1fr 1fr;
    gap: 15px;
    padding: 15px 0;
    border-bottom: 1px solid rgba(0, 0, 0, 0.15);
    align-items: center;
    font-family: NeueMontrealRegular, sans-serif;
    font-size: 0.9rem;
    color: black;
}

.merch-row:hover {
    background: rgba(252, 180, 22, 0.1);
}

.merch-row .col-name {
    font-family: NeueMontrealBold, sans-serif;
}

.merch-row .col-status {
    opacity: 0.5;
    font-size: 0.85rem;
}

.merch-row .col-status.available {
    opacity: 1;
    color: black;
}

.merch-row .col-link a {
    color: #fcb416;
    text-decoration: none;
    font-family: BNAxelGroteskBold, sans-serif;
    font-size: 0.8rem;
    letter-spacing: 1px;
}

.merch-row .col-link a:hover {
    color: black;
    animation: shake 1s infinite;
}

.merch-row .col-actions {
    display: flex;
    gap: 15px;
}

.merch-preview {
    width: 60px;
    height: 60px;
    flex-shrink: 0;
}

.merch-preview img {
    width: 100%;
    height: 100%;
    object-fit: cover;
}

.merch-preview .no-image {
    width: 100%;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    background: rgba(0, 0, 0, 0.1);
    font-family: BNAxelGroteskBold, sans-serif;
    font-size: 0.5rem;
    color: black;
    opacity: 0.5;
}

/* Image Upload Styling */
.image-upload-container {
    margin-bottom: 15px;
}

.image-preview {
    width: 150px;
    height: 150px;
    margin-bottom: 15px;
    background: rgba(0, 0, 0, 0.1);
    display: flex;
    align-items: center;
    justify-content: center;
    overflow: hidden;
}

.image-preview img {
    max-width: 100%;
    max-height: 100%;
    object-fit: contain;
}

.image-upload-actions {
    display: flex;
    align-items: stretch;
    gap: 15px;
    flex-wrap: wrap;
}

.upload-btn,
.remove-btn {
    font-family: BNAxelGroteskBold, sans-serif;
    padding: 10px 20px;
    background: #fcb416;
    color: black;
    border: none;
    cursor: pointer;
    font-size: 0.85rem;
    letter-spacing: 1px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    height: auto;
    box-sizing: border-box;
}

.upload-btn:hover,
.remove-btn:hover {
    background: black;
    color: #fcb416;
    animation: shake 1s infinite;
}

.upload-btn input[type="file"] {
    display: none;
}

.upload-status {
    font-family: NeueMontrealRegular, sans-serif;
    font-size: 0.85rem;
    color: black;
    opacity: 0.6;
}

/* Checkbox styling */
.checkbox-label {
    display: flex;
    align-items: center;
    gap: 10px;
    font-family: BNAxelGroteskBold, sans-serif;
    font-size: 0.85rem;
    letter-spacing: 1px;
    color: black;
    cursor: pointer;
    margin-top: 10px;
    margin-bottom: 10px;
}

.checkbox-label input[type="checkbox"] {
    width: 20px;
    height: 20px;
    margin: 0;
    padding: 0;
    accent-color: #fcb416;
    cursor: pointer;
}

/* Tools Section */
.tools-list {
    display: flex;
    flex-direction: column;
    gap: 30px;
}

.tool-card {
    background: transparent;
    padding: 30px 0;
    border-bottom: 1px solid rgba(0, 0, 0, 0.2);
}

.tool-card h3 {
    font-family: BNAxelGroteskBold, sans-serif;
    color: black;
    margin: 0 0 15px 0;
    font-size: 1.1rem;
    letter-spacing: 1px;
}

.tool-description {
    font-family: NeueMontrealRegular, sans-serif;
    font-size: 0.9rem;
    color: #fcb416;
    margin: 0 0 20px 0;
    max-width: 500px;
}

.tool-actions {
    display: flex;
    gap: 20px;
    flex-wrap: wrap;
}

.tool-btn {
    font-family: BNAxelGroteskBold, sans-serif;
    padding: 12px 24px;
    background: #fcb416;
    color: black;
    border: none;
    cursor: pointer;
    font-size: 0.9rem;
    letter-spacing: 1px;
}

.tool-btn:hover:not(:disabled) {
    background: black;
    color: #fcb416;
    animation: shake 1s infinite;
}

.tool-btn:disabled {
    opacity: 0.4;
    cursor: not-allowed;
}

.tool-btn.danger {
    background: transparent;
    color: black;
    border: 2px solid rgba(0, 0, 0, 0.3);
}

.tool-btn.danger:hover:not(:disabled) {
    background: #8B0000;
    color: white;
    border-color: #8B0000;
    animation: shake 1s infinite;
}

/* Shake Animation */
@keyframes shake {
    0% {
        transform: translate(0px, 0px) rotate(0deg);
    }

    10% {
        transform: translate(-1px, 0px) rotate(-1deg);
    }

    20% {
        transform: translate(1px, 0px) rotate(1deg);
    }

    30% {
        transform: translate(-1px, 0px) rotate(-1deg);
    }

    40% {
        transform: translate(1px, 0px) rotate(1deg);
    }

    50% {
        transform: translate(-1px, 0px) rotate(-1deg);
    }

    60% {
        transform: translate(1px, 0px) rotate(1deg);
    }

    70% {
        transform: translate(-1px, 0px) rotate(-1deg);
    }

    80% {
        transform: translate(1px, 0px) rotate(1deg);
    }

    90% {
        transform: translate(-1px, 0px) rotate(-1deg);
    }

    100% {
        transform: translate(0px, 0px) rotate(0deg);
    }
}

/* Responsive */
@media screen and (max-width: 1200px) {
    .shows-header {
        display: none;
    }

    .shows-row {
        display: flex;
        flex-direction: column;
        gap: 8px;
        padding: 20px 0;
    }

    .shows-row .col-date {
        font-size: 1.1rem;
    }

    .shows-row .col-event,
    .shows-row .col-support {
        font-size: 0.9rem;
        opacity: 0.7;
    }

    .shows-row .col-event::before {
        content: "Event: ";
        font-family: BNAxelGroteskBold, sans-serif;
        font-size: 0.7rem;
        letter-spacing: 1px;
    }

    .shows-row .col-support::before {
        content: "Support: ";
        font-family: BNAxelGroteskBold, sans-serif;
        font-size: 0.7rem;
        letter-spacing: 1px;
    }

    /* Merch table responsive */
    .merch-header {
        display: none;
    }

    .merch-row {
        display: flex;
        flex-direction: row;
        flex-wrap: wrap;
        gap: 15px;
        padding: 20px 0;
        align-items: center;
    }

    .merch-row .col-image {
        flex: 0 0 auto;
    }

    .merch-row .col-name {
        flex: 1;
        font-size: 1rem;
    }

    .merch-row .col-status {
        flex: 0 0 auto;
    }

    .merch-row .col-link {
        flex: 0 0 auto;
    }

    .merch-row .col-actions {
        flex: 0 0 100%;
        margin-top: 10px;
    }

    .shows-row .col-actions {
        margin-top: 10px;
    }
}

@media screen and (max-width: 768px) {
    .modal-overlay {
        padding: 10px;
        align-items: flex-start;
    }

    .modal {
        padding: 20px;
        margin: 10px;
        max-width: 100%;
    }

    .modal-form-grid {
        grid-template-columns: 1fr;
        gap: 10px;
    }

    .modal-wide {
        max-width: 100%;
        padding: 20px;
    }

    .admin-header {
        flex-direction: column;
        gap: 15px;
        text-align: center;
    }

    .admin-nav {
        padding: 0 15px;
        flex-wrap: wrap;
        justify-content: center;
    }

    .admin-nav button {
        padding: 12px 15px;
        font-size: 0.8rem;
    }

    .merch-card {
        flex-direction: column;
        align-items: flex-start;
        gap: 15px;
    }

    .merch-info {
        flex-direction: column;
        align-items: flex-start;
        gap: 5px;
    }

    .merch-actions {
        width: 100%;
    }

    .tool-actions {
        flex-direction: column;
    }

    .tool-btn {
        width: 100%;
    }
}
</style>
